import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Divider, Grid } from '@mui/material';
import MyModal from '../MyModal/MyModal';
import { leaveRequestViewModalStyles } from './LeaveRequestViewModal.styles';
import { formatDateTR, formatDateTime } from '../../utils/dateUtils';

const LeaveRequestViewModal = ({ open, onClose, leaveRequest }) => {
  const { t } = useTranslation();

  if (!leaveRequest) return null;

  const getStatusChip = (status, isTeamApproved, isGroupApproved) => {
    if (status === 'REJECTED') {
      return <Chip label={t('leaves.status.rejected')} color="error" size="small" />;
    }
    if (status === 'APPROVED') {
      return <Chip label={t('leaves.status.approved')} color="success" size="small" />;
    }
    if (isGroupApproved === false) {
      return <Chip label={t('leaves.status.groupRejected')} color="error" size="small" />;
    }
    if (isTeamApproved === false) {
      return <Chip label={t('leaves.status.teamRejected')} color="error" size="small" />;
    }
    if (isGroupApproved === true) {
      return <Chip label={t('leaves.status.groupApproved')} color="info" size="small" />;
    }
    if (isTeamApproved === true) {
      return <Chip label={t('leaves.status.teamApproved')} color="warning" size="small" />;
    }
    return <Chip label={t(`leaves.status.${status?.toLowerCase?.()}`)} color="default" size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if it's a datetime (has time component)
      if (dateString.includes('T') || dateString.includes(' ')) {
        return formatDateTime(date);
      }
      return formatDateTR(date);
    } catch (e) {
      return dateString;
    }
  };

  return (
    <MyModal
      open={open}
      onClose={onClose}
      title={t('leaves.view.title', 'İzin Talebi Detayları')}
      maxWidth="md"
      disableBackdropClose
      disableEscapeKeyDown
    >
      <Box sx={leaveRequestViewModalStyles.container}>
        <Box sx={leaveRequestViewModalStyles.card}>
        <Box sx={leaveRequestViewModalStyles.header}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography variant="h6" sx={leaveRequestViewModalStyles.title}>
                {leaveRequest.user?.first_name} {leaveRequest.user?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {leaveRequest.leaveTypeName || t('leaves.form.leaveType', 'İzin Türü')} · {leaveRequest.duration || '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              {getStatusChip(
                leaveRequest.status,
                leaveRequest.is_team_manager_approved,
                leaveRequest.is_group_manager_approved
              )}
            </Box>
          </Box>
          <Divider sx={leaveRequestViewModalStyles.divider} />
        </Box>

        <Grid container spacing={2} sx={leaveRequestViewModalStyles.detailsContainer}>
          <Grid item xs={12} sm={6}>
            <Box sx={leaveRequestViewModalStyles.detailRow}>
              <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                {t('leaves.form.user', 'Kullanıcı')}:
              </Typography>
              <Typography variant="body2" sx={leaveRequestViewModalStyles.value}>
                {leaveRequest.user?.first_name} {leaveRequest.user?.last_name}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={leaveRequestViewModalStyles.detailRow}>
              <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                {t('leaves.form.leaveType', 'İzin Türü')}:
              </Typography>
              <Typography variant="body2" sx={leaveRequestViewModalStyles.value}>
                {leaveRequest.leaveTypeName || '-'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={leaveRequestViewModalStyles.detailRow}>
              <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                {t('leaves.form.startDate', 'Başlangıç Tarihi')}:
              </Typography>
              <Typography variant="body2" sx={leaveRequestViewModalStyles.value}>
                {formatDate(leaveRequest.startDate)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={leaveRequestViewModalStyles.detailRow}>
              <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                {t('leaves.form.endDate', 'İş Başı Tarihi')}:
              </Typography>
              <Typography variant="body2" sx={leaveRequestViewModalStyles.value}>
                {formatDate(leaveRequest.endDate)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={leaveRequestViewModalStyles.detailRow}>
              <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                {t('leaves.table.duration', 'Süre')}:
              </Typography>
              <Typography variant="body2" sx={leaveRequestViewModalStyles.value}>
                {leaveRequest.duration || '-'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={leaveRequestViewModalStyles.detailRow}>
              <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                {t('leaves.table.status', 'Durum')}:
              </Typography>
              <Box>
                {getStatusChip(
                  leaveRequest.status,
                  leaveRequest.is_team_manager_approved,
                  leaveRequest.is_group_manager_approved
                )}
              </Box>
            </Box>
          </Grid>

          {leaveRequest.approvingManager && (
            <Grid item xs={12} sm={6}>
              <Box sx={leaveRequestViewModalStyles.detailRow}>
                <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
                  {t('leaves.table.approvingManager', 'Onaylayan Yönetici')}:
                </Typography>
                <Typography variant="body2" sx={leaveRequestViewModalStyles.value}>
                  {leaveRequest.approvingManager.first_name} {leaveRequest.approvingManager.last_name}
                </Typography>
              </Box>
            </Grid>
          )}

        </Grid>

        {leaveRequest.description && (
          <Box sx={leaveRequestViewModalStyles.detailRowWithDescription}>
            <Typography variant="subtitle2" sx={leaveRequestViewModalStyles.label}>
              {t('leaves.form.description', 'Açıklama')}:
            </Typography>
            <Typography variant="body2" sx={leaveRequestViewModalStyles.descriptionValue}>
              {leaveRequest.description}
            </Typography>
          </Box>
        )}

        </Box>
      </Box>
    </MyModal>
  );
};

export default LeaveRequestViewModal;

